import { useLocalStorage } from '@/hooks/browser/use-local-storage';
import { useAnalytics } from '@/services/analytics';
import { Box, Button, Divider, Link, Scrollable, Text } from '@customerio/pluma-components/react';
import { m, useReducedMotion } from 'motion/react';
import { useCallback, useRef, useState } from 'react';
import * as chatStyles from '../../chat.css';
import type { ResolvedSuggestion, SuggestionState } from '../suggestions';
import { Suggestions } from '../suggestions';

const SUGGESTIONS_ANIMATION_DURATION = 0.24;
const SUGGESTIONS_ANIMATION_EASE: [number, number, number, number] = [0.2, 0, 0.38, 0.9];
const SUGGESTIONS_TRANSITION = {
  duration: SUGGESTIONS_ANIMATION_DURATION,
  ease: SUGGESTIONS_ANIMATION_EASE,
} as const;

type NextMessageSuggestionsProps = {
  state: SuggestionState;
  onSuggestionClick: (message: string, index: number, suggestion: ResolvedSuggestion) => void;
  shouldIncludeHumanSupport?: boolean;
};

export function NextMessageSuggestions({
  state,
  onSuggestionClick,
  shouldIncludeHumanSupport = false,
}: NextMessageSuggestionsProps) {
  const scrollableRef = useRef<HTMLDivElement>(null);
  const [suggestionsHidden, setSuggestionsHidden] = useLocalStorage('copilot-suggestions-hidden', false);
  const [isCollapsing, setIsCollapsing] = useState(false);
  const [isExpanding, setIsExpanding] = useState(false);
  const { track } = useAnalytics();
  const shouldReduceMotion = useReducedMotion();

  const isContentVisible = !suggestionsHidden || isCollapsing;
  const isContentInert = isCollapsing || isExpanding;

  const handleToggleSuggestions = useCallback(() => {
    if (suggestionsHidden) {
      setSuggestionsHidden(false);
      setIsExpanding(true);
      track('aiAssistant', 'suggestionsShown', {});
    } else {
      track('aiAssistant', 'suggestionsHidden', {});
      if (shouldReduceMotion === true) {
        setSuggestionsHidden(true);
      } else {
        setIsCollapsing(true);
      }
    }
  }, [suggestionsHidden, shouldReduceMotion, setSuggestionsHidden, track]);

  const handleCollapseComplete = useCallback(() => {
    setSuggestionsHidden(true);
    setIsCollapsing(false);
  }, [setSuggestionsHidden]);

  const handleExpandComplete = useCallback(() => {
    setIsExpanding(false);
  }, []);

  if (state.isVisible === false || state.isLoading === true) {
    return null;
  }

  return (
    <m.div
      initial={{ opacity: 0, y: shouldReduceMotion === true ? 0 : 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={shouldReduceMotion === true ? { duration: 0 } : SUGGESTIONS_TRANSITION}
    >
      <Box flexShrink="0">
        <Divider />
        <Box display="flex" alignItems="center" justifyContent="space-between" px="150" pt="100">
          <Text size="xs">
            <Link
              icon={suggestionsHidden ? 'view-on' : 'view-off'}
              isUnderlined={false}
              variant="secondary"
              onClick={handleToggleSuggestions}
              aria-expanded={!suggestionsHidden}
            >
              {suggestionsHidden ? 'Show suggestions' : 'Hide suggestions'}
            </Link>
          </Text>
          <Box
            {...(suggestionsHidden || isContentInert ? { inert: true, style: { visibility: 'hidden' as const } } : {})}
          >
            <Button
              isIconOnly={true}
              icon="chevron-left"
              variant="tertiary"
              size="sm"
              aria-label="Scroll suggestions left"
              onClick={() => {
                if (scrollableRef.current != null) {
                  scrollableRef.current.scrollTo({
                    left: scrollableRef.current.scrollLeft - scrollableRef.current.clientWidth,
                    behavior: 'smooth',
                  });
                }
              }}
            />
            <Button
              isIconOnly={true}
              icon="chevron-right"
              variant="tertiary"
              size="sm"
              aria-label="Scroll suggestions right"
              onClick={() => {
                if (scrollableRef.current != null) {
                  scrollableRef.current.scrollTo({
                    left: scrollableRef.current.scrollLeft + scrollableRef.current.clientWidth,
                    behavior: 'smooth',
                  });
                }
              }}
            />
          </Box>
        </Box>
        {isContentVisible ? (
          <m.div
            initial={isExpanding ? { gridTemplateRows: '0fr' } : false}
            animate={{ gridTemplateRows: isCollapsing ? '0fr' : '1fr' }}
            transition={shouldReduceMotion === true ? { duration: 0 } : SUGGESTIONS_TRANSITION}
            className={chatStyles.suggestionsCollapseGrid}
            onAnimationComplete={() => {
              if (isCollapsing) {
                handleCollapseComplete();
              }
              if (isExpanding) {
                handleExpandComplete();
              }
            }}
            {...(isContentInert ? { inert: true } : {})}
          >
            <div className={chatStyles.suggestionsCollapseContent}>
              <Scrollable
                overflow="x"
                withEdgeIndicators
                display="flex"
                gap="100"
                ref={scrollableRef}
                p="100"
                className={chatStyles.hiddenScrollbar}
              >
                <Suggestions
                  suggestions={state.suggestions}
                  onSuggestionClick={onSuggestionClick}
                  shouldShowPlaceholder={false}
                  shouldIncludeHumanSupport={shouldIncludeHumanSupport}
                />
              </Scrollable>
            </div>
          </m.div>
        ) : null}
      </Box>
    </m.div>
  );
}
